import { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import ImageLightbox, { useLightbox } from '../components/ImageLightbox'
import SmartDateInput from '../components/SmartDateInput'
import {
  Box, Text, Button, Badge, Group, Modal, Stack, Textarea,
  TextInput, Tooltip, Center, Loader, SimpleGrid,
  Table, ScrollArea, Divider, Tabs, Paper, FileButton, Anchor, Image,
  ActionIcon, Card, Menu,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconFileText, IconAlertTriangle, IconCheck, IconSearch,
  IconHistory, IconClipboardX, IconPaperclip, IconUpload, IconX,
  IconCalendar, IconUser, IconPhoto, IconQuestionMark, IconEdit, IconDotsVertical,
} from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { getAssets, uploadDamagedIncident, uploadLostIncident, getRepairImagesByAsset, updateDamagedInfo, getRepairsByAsset, scanLookupAsset, API_BASE_URL } from '../api/client'
import api from '../api/client'
import AssetQuickView from '../components/assets/AssetQuickView'
import jsPDF from 'jspdf'
import { drawHeader, urlToBase64, drawSection, drawRow2, drawSignatures, drawFooter, registerThaiFont, PDF_FONT , openPdfInNewTab} from '../assets/pdfHelper'

// ── Download via backend proxy (preserves filename + extension) ──────────────
const BACKEND = API_BASE_URL
function incidentDownloadUrl(cloudinaryUrl: string, assetCode: string, originalFilename?: string) {
  const filename = originalFilename || `Incident_${assetCode}`
  return `${BACKEND}/file-proxy?url=${encodeURIComponent(cloudinaryUrl)}&filename=${encodeURIComponent(filename)}`
}

async function generateWriteOffPDF(asset: any, form: any, logoB64?: string | null, companyName?: string | null) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' })
  registerThaiFont(pdf)
  const W = 210, M = 15
  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
  const ref = `WO-${String(asset.id).padStart(5, '0')}`

  let y = await drawHeader(pdf, 'ASSET WRITE-OFF REQUEST', '(Property Disposal Report)', ref, today, logoB64, companyName ?? undefined)
  y = drawSection(pdf, 'Asset Information', y)
  y = drawRow2(pdf, 'Asset Code:', asset.assetCode, 'Asset Name:', asset.name, y)
  y = drawRow2(pdf, 'Serial Number:', asset.serialNumber ?? '-', 'Department:', asset.departmentCode ?? '-', y)
  y = drawRow2(pdf,
    'Purchase Date:', asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString('en-GB') : '-',
    'Purchase Price:', asset.purchasePrice ? `THB ${Number(asset.purchasePrice).toLocaleString()}` : '-', y)
  y = drawRow2(pdf, 'Location:', asset.location ?? '-', 'Category:', [asset.category, asset.subCategory].filter(Boolean).join(' / ') || '-', y)
  y += 2

  // แสดงเหตุผลความเสียหายต้นทาง (จากตอน mark damaged) แยกจากเหตุผล write-off
  // เพื่อให้ผู้อนุมัติเห็นบริบทครบทั้งสองชั้น: ทำไมเสียหาย → ทำไมถึงตัดจำหน่าย
  if (asset.damagedNote || asset.damagedDate) {
    y = drawSection(pdf, 'Original Damage Report', y)
    pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
    if (asset.damagedDate) {
      pdf.text(`Damaged Date: ${new Date(asset.damagedDate).toLocaleDateString('en-GB')}`, M + 3, y); y += 5
    }
    const damageLines = pdf.splitTextToSize(asset.damagedNote || '-', W - M * 2 - 6)
    pdf.text(damageLines, M + 3, y); y += damageLines.length * 5 + 6
  }

  y = drawSection(pdf, 'Reason / Justification for Write-Off', y)
  pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
  const reasonLines = pdf.splitTextToSize(form.reason || '-', W - M * 2 - 6)
  pdf.text(reasonLines, M + 3, y); y += reasonLines.length * 5 + 6

  if (form.detail) {
    y = drawSection(pdf, 'Additional Details', y)
    const detailLines = pdf.splitTextToSize(form.detail, W - M * 2 - 6)
    pdf.text(detailLines, M + 3, y); y += detailLines.length * 5 + 6
  }

  y = drawSection(pdf, 'Current Condition', y)
  pdf.setFont(PDF_FONT, 'normal'); pdf.setFontSize(9)
  pdf.text(`Asset Status: ${asset.status}`, M + 3, y); y += 6
  if (form.maintenanceRef) { pdf.text(`Maintenance Reference: ${form.maintenanceRef}`, M + 3, y); y += 6 }
  y += 4

  pdf.setDrawColor(220, 220, 220); pdf.line(M, y, W - M, y); y += 6
  pdf.setFont('helvetica', 'italic'); pdf.setFontSize(7.5); pdf.setTextColor(100, 100, 100)
  pdf.text('This document requests authorization to permanently remove the above asset from the property inventory.', M + 2, y); y += 4.5
  pdf.text('All approvals must be obtained before the asset is disposed of. Unauthorized disposal is strictly prohibited.', M + 2, y)
  pdf.setTextColor(0, 0, 0); y += 10

  y = drawSection(pdf, 'Authorized Signatures', y)
  drawSignatures(pdf, [
    { title: 'Prepared By',         sub: '(Asset Manager / User)'   },
    { title: 'Head of Department',  sub: '(Dept. Head)'             },
    { title: 'Cost Controller',     sub: '(Finance)'                },
    { title: 'Director of Finance', sub: '(Finance Director)'       },
    { title: 'Hotel Manager',       sub: '(Hotel Manager)'          },
    { title: 'General Manager',     sub: '(General Manager)'        },
  ], y)

  drawFooter(pdf, ref, today)
  openPdfInNewTab(pdf, `${ref}.pdf`)
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  if (status === 'DAMAGED')   return <Badge color="red"  variant="light" size="sm">{t('assetStatus.damaged','DAMAGED')}</Badge>
  if (status === 'WRITE_OFF') return <Badge color="dark" variant="filled" size="sm">{t('assetStatus.writeOff','WRITE OFF')}</Badge>
  return <Badge color="gray" variant="light" size="sm">{status}</Badge>
}

// ── Repair Photos thumbnail strip ─────────────────────────────────────────
function RepairPhotoStrip({ assetId, t }: { assetId: number; t: any }) {
  const [photos, setPhotos] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const { lightbox, openLightbox, closeLightbox } = useLightbox()

  useEffect(() => {
    getRepairImagesByAsset(assetId)
      .then(res => {
        const all = (res.data as any[]).flatMap(j => j.images ?? [])
        setPhotos(all)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [assetId])

  if (loading) return <Loader size="xs" />
  if (photos.length === 0) return <Text size="xs" c="dimmed">—</Text>

  return (
    <>
      <Group gap={4} wrap="nowrap">
        {photos.slice(0, 3).map((url, i) => (
          <Image key={i} src={url} w={32} h={32} radius="sm" fit="cover"
            style={{ cursor: 'pointer', border: '1px solid #e2e8f0' }}
            onClick={() => openLightbox(photos, i)} />
        ))}
        {photos.length > 3 && (
          <Text size="xs" c="dimmed" style={{ cursor: 'pointer' }}
            onClick={() => openLightbox(photos, 3)}>
            +{photos.length - 3}
          </Text>
        )}
      </Group>
      <ImageLightbox
        images={lightbox?.images ?? []}
        initialIndex={lightbox?.index ?? 0}
        opened={lightbox !== null}
        onClose={closeLightbox}
      />
    </>
  )
}

// แสดง consumable/part ที่เคยถูกเบิกใช้ในทุก repair job ของ asset นี้ (รวมทุก job ไม่ว่าจะปิดงานแล้วหรือยัง)
// เพื่อให้ผู้อนุมัติ write-off เห็นว่าเคยพยายามซ่อมด้วยอะไรไปแล้วก่อนตัดสินใจตัดจำหน่าย
function RepairPartsUsed({ assetCode }: { assetCode: string }) {
  const [parts, setParts] = useState<{ name: string; qty: number }[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!assetCode) { setLoading(false); return }
    getRepairsByAsset(assetCode)
      .then(res => {
        const merged = new Map<string, number>()
        ;(res.data as any[]).forEach(j => {
          (j.consumablesUsed ?? []).forEach((c: any) => {
            merged.set(c.name, (merged.get(c.name) ?? 0) + c.qty)
          })
        })
        setParts([...merged.entries()].map(([name, qty]) => ({ name, qty })))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [assetCode])

  if (loading) return <Loader size="xs" />
  if (parts.length === 0) return <Text size="xs" c="dimmed">—</Text>

  return (
    <Group gap={4} wrap="wrap" style={{ maxWidth: 160 }}>
      {parts.map((p, i) => (
        <Badge key={i} size="xs" color="grape" variant="light">{p.name} × {p.qty}</Badge>
      ))}
    </Group>
  )
}

export default function WriteOffPage() {
  const { isSmall, isMobile } = useBreakpoint()
  const { roles, departments: userDepts , companyLogoUrl, companyName} = useAuth()
  const { t } = useTranslation()
  const isAdminLevel = roles.includes('ADMIN') || roles.includes('ASSET_MANAGER')

  const [damaged,    setDamaged]    = useState<any[]>([])
  const [writtenOff, setWrittenOff] = useState<any[]>([])
  const [lost,       setLost]       = useState<any[]>([])
  const [loading,    setLoading]    = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const [search,     setSearch]     = useState('')
  const [qvAsset, setQvAsset] = useState<any>(null)
  const [qvOpen, setQvOpen] = useState(false)
  const openQv = (code: string) => scanLookupAsset(code).then(r => { if (r.data?.found) { setQvAsset(r.data); setQvOpen(true) } })
  const [activeTab,  setActiveTab]  = useState<string | null>('damaged')

  // deep link: ?asset=CODE จาก share ให้ค้นหาทันทีและเลือก tab ที่ถูกต้อง
  useEffect(() => {
    const code = searchParams.get('asset')
    if (code) {
      setSearch(code)
      setSearchParams({})
    }
  }, [])
  const [selected,   setSelected]   = useState<any>(null)
  const [form,       setForm]       = useState({ reason: '', detail: '', maintenanceRef: '' })
  const [saving,     setSaving]     = useState(false)
  const [opened, { open, close }]   = useDisclosure(false)
  const [incidentFile,   setIncidentFile]   = useState<File | null>(null)
  const resetIncidentRef = useRef<() => void>(() => {})
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const [editingDamaged, setEditingDamaged] = useState<any>(null)
  const [editDamagedForm, setEditDamagedForm] = useState({ damagedDate: '', damagedNote: '' })
  const [savingDamaged, setSavingDamaged] = useState(false)

  const applyDeptScope = (list: any[]) => {
    if (isAdminLevel || userDepts.length === 0) return list
    return list.filter((a: any) => userDepts.includes(a.departmentCode))
  }

  const load = async () => {
    setLoading(true)
    try {
      const [dmgRes, woRes, lostRes] = await Promise.all([
        getAssets('DAMAGED'),
        getAssets('WRITE_OFF'),
        getAssets('LOST'),
      ])
      setDamaged(applyDeptScope(dmgRes.data ?? []))
      setWrittenOff(applyDeptScope(woRes.data ?? []))
      setLost(applyDeptScope(lostRes.data ?? []))
    } catch { } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const filtered = (list: any[]) => list.filter(a =>
    !search ||
    a.assetCode?.toLowerCase().includes(search.toLowerCase()) ||
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.serialNumber?.toLowerCase().includes(search.toLowerCase())
  )

  const openWriteOff = (asset: any) => {
    setSelected(asset)
    setForm({ reason: '', detail: '', maintenanceRef: '' })
    setIncidentFile(null)
    open()
  }

  const openEditDamaged = (asset: any) => {
    setEditingDamaged(asset)
    setEditDamagedForm({
      damagedDate: asset.damagedDate ? new Date(asset.damagedDate).toISOString().slice(0, 10) : '',
      damagedNote: asset.damagedNote ?? '',
    })
  }

  const saveDamagedInfo = async () => {
    if (!editingDamaged) return
    setSavingDamaged(true)
    try {
      await updateDamagedInfo(editingDamaged.id, editDamagedForm)
      notifications.show({ color: 'teal', message: t('writeoff.damageInfoUpdated') })
      setEditingDamaged(null)
      load()
    } catch (err: any) {
      notifications.show({ color: 'red', message: err?.response?.data?.message ?? t('writeoff.uploadFailed') })
    } finally { setSavingDamaged(false) }
  }

  const handleUploadIncident = async (asset: any, file: File) => {
    setUploadingId(asset.id)
    try {
      await uploadDamagedIncident(asset.id, file)
      notifications.show({ color: 'teal', title: t('writeoff.uploadSuccess'), message: asset.assetCode })
      load()
    } catch {
      notifications.show({ color: 'red', message: t('writeoff.uploadFailed') })
    } finally { setUploadingId(null) }
  }

  const handleUploadLostIncident = async (asset: any, file: File) => {
    setUploadingId(asset.id)
    try {
      await uploadLostIncident(asset.id, file)
      notifications.show({ color: 'teal', title: t('writeoff.uploadSuccess'), message: asset.assetCode })
      load()
    } catch {
      notifications.show({ color: 'red', message: t('writeoff.uploadFailed') })
    } finally { setUploadingId(null) }
  }

  const handleConfirmWriteOff = async () => {
    if (!form.reason.trim()) {
      notifications.show({ color: 'red', message: t('writeoff.reasonRequired') }); return
    }
    modals.openConfirmModal({
      title: t('writeoff.confirmTitle'),
      children: (
        <Text size="sm">
          {t('writeoff.confirmMsg')} <strong>{selected?.assetCode} — {selected?.name}</strong>?
          {' '}{t('writeoff.confirmNote')}
        </Text>
      ),
      labels: { confirm: t('writeoff.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setSaving(true)
        try {
          generateWriteOffPDF(selected, form, await (companyLogoUrl ? urlToBase64(companyLogoUrl) : Promise.resolve(null)), companyName ?? null)
          await api.patch(`/assets/${selected.id}/write-off`, {
            reason: form.reason,
            detail: form.detail,
          })
          if (incidentFile) {
            try {
              await uploadDamagedIncident(selected.id, incidentFile)
            } catch {
              notifications.show({ color: 'orange', message: t('writeoff.writeOffUploadFailed') })
            }
          }
          notifications.show({ color: 'teal', message: t('writeoff.writeOffSuccess') })
          close(); load()
        } catch {
          generateWriteOffPDF(selected, form, await (companyLogoUrl ? urlToBase64(companyLogoUrl) : Promise.resolve(null)), companyName ?? null)
          notifications.show({ color: 'blue', message: t('writeoff.pdfOnly') })
          close()
        } finally { setSaving(false) }
      },
    })
  }

  const thStyle = { fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' as const }

  // ── File cell with upload/replace button ────────────────────────────────
  const FileCell = ({ asset }: { asset: any }) => (
    <Table.Td>
      {asset.damagedIncidentUrl?.trim() ? (
        <Group gap={6} wrap="nowrap">
          <Anchor href={incidentDownloadUrl(asset.damagedIncidentUrl, asset.assetCode, asset.damagedIncidentFilename)} target="_blank" size="xs">
            <Group gap={4}><IconPaperclip size={12} />{t('writeoff.viewFile')}</Group>
          </Anchor>
          <FileButton onChange={(f) => { if (f) handleUploadIncident(asset, f) }}
            accept="application/pdf,image/jpeg,image/png,image/webp">
            {(props) => (
              <Tooltip label={t('writeoff.replaceFile')}>
                <Button {...props} size="xs" variant="subtle" color="gray" p={4} loading={uploadingId === asset.id}>
                  <IconUpload size={12} />
                </Button>
              </Tooltip>
            )}
          </FileButton>
        </Group>
      ) : (
        <FileButton onChange={(f) => { if (f) handleUploadIncident(asset, f) }}
          accept="application/pdf,image/jpeg,image/png,image/webp">
          {(props) => (
            <Button {...props} size="xs" variant="light" color="gray"
              leftSection={<IconUpload size={12} />}
              loading={uploadingId === asset.id}>
              {t('writeoff.attachFile')}
            </Button>
          )}
        </FileButton>
      )}
    </Table.Td>
  )

  // ── การ์ดสำหรับแท็บ Damaged บนมือถือ — ปุ่ม Write Off เห็นเสมอ ส่วนรายละเอียดอื่น (แผนก/สถานที่/
  // เหตุผลความเสียหาย/ไฟล์แนบ/รูปซ่อม/อะไหล่ที่ใช้) พับไว้ กดที่การ์ดเพื่อขยายดู — ใช้ closure เดียว
  // กับ FileCell ด้านบน ไม่ต้อง prop-drill callback/state ต่างๆ ผ่านหลายชั้น
  const DamagedCard = ({ asset: a }: { asset: any }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <Card withBorder radius="lg" p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs" onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text fw={600} size="sm" style={{ color: '#0d9488' }}
              onClick={(e) => { e.stopPropagation(); openQv(a.assetCode) }}>{a.assetCode}</Text>
            <Text size="xs" c="dimmed">{a.name}</Text>
            <Text size="xs" c="dimmed">{a.category}</Text>
          </Box>
          <StatusBadge status={a.status} />
        </Group>
        {expanded && (
          <>
            <Divider my={8} />
            <Stack gap={5}>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.serialNo')}</Text><Text size="xs">{a.serialNumber ?? '-'}</Text></Group>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.department')}</Text><Text size="xs">{a.departmentCode ?? '-'}</Text></Group>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.location')}</Text><Text size="xs">{a.location ?? '-'}</Text></Group>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.purchasePrice')}</Text><Text size="xs">{a.purchasePrice ? `฿${Number(a.purchasePrice).toLocaleString()}` : '-'}</Text></Group>
              <Group gap={6} wrap="nowrap" align="flex-start">
                <Text size="xs" c="dimmed" style={{ minWidth: 90, flexShrink: 0 }}>{t('writeoff.damageReason')}</Text>
                <Group gap={6} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                  <Text size="xs">{a.damagedNote || '—'}</Text>
                  <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => openEditDamaged(a)}><IconEdit size={12} /></ActionIcon>
                </Group>
              </Group>
              <Box onClick={(e) => e.stopPropagation()}>
                <Text size="xs" c="dimmed" mb={4}>{t('writeoff.attachedFile')}</Text>
                {a.damagedIncidentUrl?.trim() ? (
                  <Group gap={6} wrap="nowrap">
                    <Anchor href={incidentDownloadUrl(a.damagedIncidentUrl, a.assetCode, a.damagedIncidentFilename)} target="_blank" size="xs">
                      <Group gap={4}><IconPaperclip size={12} />{t('writeoff.viewFile')}</Group>
                    </Anchor>
                    <FileButton onChange={(f) => { if (f) handleUploadIncident(a, f) }} accept="application/pdf,image/jpeg,image/png,image/webp">
                      {(props) => <Button {...props} size="xs" variant="subtle" color="gray" p={4} loading={uploadingId === a.id}><IconUpload size={12} /></Button>}
                    </FileButton>
                  </Group>
                ) : (
                  <FileButton onChange={(f) => { if (f) handleUploadIncident(a, f) }} accept="application/pdf,image/jpeg,image/png,image/webp">
                    {(props) => <Button {...props} size="xs" variant="light" color="gray" leftSection={<IconUpload size={12} />} loading={uploadingId === a.id}>{t('writeoff.attachFile')}</Button>}
                  </FileButton>
                )}
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>{t('writeoff.repairPhotos')}</Text>
                <RepairPhotoStrip assetId={a.id} t={t} />
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>{t('writeoff.partsUsed')}</Text>
                <RepairPartsUsed assetCode={a.assetCode} />
              </Box>
            </Stack>
          </>
        )}
        <Button size="xs" variant="light" color="red" fullWidth mt={10}
          leftSection={<IconFileText size={13} />}
          onClick={(e) => { e.stopPropagation(); openWriteOff(a) }}>
          {t('writeoff.writeOff')}
        </Button>
      </Card>
    )
  }

  // ── การ์ดสำหรับแท็บ Lost บนมือถือ — ปุ่ม Write Off ยังเห็นเสมอ ส่วนวันที่หาย/หมายเหตุ/ไฟล์แนบ
  // เหตุการณ์ พับไว้ กดที่การ์ดเพื่อขยายดู
  const LostCard = ({ asset: a }: { asset: any }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <Card withBorder radius="lg" p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs" onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text fw={600} size="sm">{a.assetCode}</Text>
            <Text size="xs" c="dimmed">{a.name}</Text>
            <Text size="xs" c="dimmed">{a.serialNumber}</Text>
          </Box>
          <Text size="xs" c="dimmed">{a.lostDate ? new Date(a.lostDate).toLocaleDateString('en-GB') : '—'}</Text>
        </Group>
        {expanded && (
          <>
            <Divider my={8} />
            <Stack gap={5}>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.department')}</Text><Text size="xs">{a.departmentCode}</Text></Group>
              <Group gap={6} wrap="nowrap" align="flex-start"><Text size="xs" c="dimmed" style={{ minWidth: 90, flexShrink: 0 }}>{t('writeoff.lostNote')}</Text><Text size="xs">{a.lostNote || '—'}</Text></Group>
              <Box onClick={(e) => e.stopPropagation()}>
                <Text size="xs" c="dimmed" mb={4}>{t('writeoff.lostIncidentFile')}</Text>
                {a.lostIncidentUrl?.trim() ? (
                  <Group gap={6} wrap="nowrap">
                    <Anchor
                      href={`${BACKEND}/file-proxy?url=${encodeURIComponent(a.lostIncidentUrl)}&filename=${encodeURIComponent(a.lostIncidentFilename || `Incident_${a.assetCode}`)}`}
                      target="_blank" size="xs">
                      <Group gap={4}><IconPaperclip size={12} />{t('writeoff.viewFile')}</Group>
                    </Anchor>
                    <FileButton onChange={(f) => { if (f) handleUploadLostIncident(a, f) }} accept="*/*">
                      {(props) => <Button {...props} size="xs" variant="subtle" color="gray" p={4} loading={uploadingId === a.id}><IconUpload size={12} /></Button>}
                    </FileButton>
                  </Group>
                ) : (
                  <FileButton onChange={(f) => { if (f) handleUploadLostIncident(a, f) }} accept="*/*">
                    {(props) => <Button {...props} size="xs" variant="light" color="gray" leftSection={<IconUpload size={12} />} loading={uploadingId === a.id}>{t('writeoff.attachFile')}</Button>}
                  </FileButton>
                )}
              </Box>
            </Stack>
          </>
        )}
        <Button size="xs" color="red" variant="light" fullWidth mt={10}
          onClick={(e) => { e.stopPropagation(); openWriteOff(a) }}>
          {t('writeoff.writeOffLost')}
        </Button>
      </Card>
    )
  }

  // ── การ์ดสำหรับแท็บ Written Off History บนมือถือ — ไม่มี action ต่อแถวเลย (เป็นประวัติอย่างเดียว)
  // เลยไม่ต้องมีเมนู ⋮ กดที่การ์ดเพื่อขยายดูรายละเอียดที่เหลือได้เหมือน ReportCard
  const WrittenOffCard = ({ asset: a }: { asset: any }) => {
    const [expanded, setExpanded] = useState(false)
    return (
      <Card withBorder radius="lg" p="md" onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
          <Box style={{ minWidth: 0, flex: 1 }}>
            <Text fw={600} size="sm" style={{ color: '#0d9488' }}
              onClick={(e) => { e.stopPropagation(); openQv(a.assetCode) }}>{a.assetCode}</Text>
            <Text size="xs" c="dimmed">{a.name}</Text>
            <Text size="xs" c="dimmed" lineClamp={1}>{a.writeOffReason || '—'}</Text>
          </Box>
          <Text size="xs" c="dimmed">
            {a.writeOffAt ? new Date(a.writeOffAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          </Text>
        </Group>
        {expanded && (
          <>
            <Divider my={8} />
            <Stack gap={5}>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.serialNo')}</Text><Text size="xs">{a.serialNumber ?? '-'}</Text></Group>
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.department')}</Text><Text size="xs">{a.departmentCode ?? '-'}</Text></Group>
              {a.damagedNote && <Group gap={6} wrap="nowrap" align="flex-start"><Text size="xs" c="dimmed" style={{ minWidth: 90, flexShrink: 0 }}>🛠 {t('writeoff.damageReason')}</Text><Text size="xs">{a.damagedNote}</Text></Group>}
              <Group gap={6} wrap="nowrap"><Text size="xs" c="dimmed" style={{ minWidth: 90 }}>{t('writeoff.writtenOffBy')}</Text><Text size="xs">{a.writeOffBy ?? '—'}</Text></Group>
              <Group gap={6} wrap="nowrap" align="flex-start" onClick={(e) => e.stopPropagation()}>
                <Text size="xs" c="dimmed" style={{ minWidth: 90, flexShrink: 0 }}>{t('writeoff.attachedFile')}</Text>
                {a.damagedIncidentUrl
                  ? <Anchor href={incidentDownloadUrl(a.damagedIncidentUrl, a.assetCode, a.damagedIncidentFilename)} target="_blank" size="xs">
                      <Group gap={4}><IconPaperclip size={12} />{t('writeoff.viewFile')}</Group>
                    </Anchor>
                  : <Text size="xs" c="dimmed">—</Text>}
              </Group>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>{t('writeoff.repairPhotos')}</Text>
                <RepairPhotoStrip assetId={a.id} t={t} />
              </Box>
            </Stack>
          </>
        )}
      </Card>
    )
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb="xl" align="flex-start">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, color: '#0f172a' }}>
            {t('writeoff.title')}
          </Text>
          <Text size="sm" c="dimmed">{t('writeoff.subtitle')}</Text>
        </Box>
      </Group>

      <Box mb="md">
        <TextInput
          placeholder={t('writeoff.search')}
          value={search} onChange={e => setSearch(e.target.value)}
          leftSection={<IconSearch size={14} />}
          size="sm" style={{ maxWidth: 320 }}
        />
      </Box>

      <SimpleGrid cols={{ base: 2, sm: 3 }} mb="xl" style={{ maxWidth: isSmall ? '100%' : 720 }}>
        <Paper withBorder radius="lg" p="md"
          onClick={() => { setActiveTab('damaged'); setSearch('') }}
          style={{ borderLeft: '4px solid #ef4444', cursor: 'pointer', background: activeTab === 'damaged' ? '#fef2f2' : '#fff' }}>
          <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('writeoff.damaged')}
          </Text>
          <Text size="xl" fw={800} style={{ color: '#ef4444' }}>{damaged.length}</Text>
        </Paper>
        <Paper withBorder radius="lg" p="md"
          onClick={() => { setActiveTab('lost'); setSearch('') }}
          style={{ borderLeft: '4px solid #f59e0b', cursor: 'pointer', background: activeTab === 'lost' ? '#fffbeb' : '#fff' }}>
          <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('writeoff.lost')}
          </Text>
          <Text size="xl" fw={800} style={{ color: '#f59e0b' }}>{lost.length}</Text>
        </Paper>
        <Paper withBorder radius="lg" p="md"
          onClick={() => { setActiveTab('writtenoff'); setSearch('') }}
          style={{ borderLeft: '4px solid #475569', cursor: 'pointer', background: activeTab === 'writtenoff' ? '#f1f5f9' : '#fff' }}>
          <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('writeoff.writtenOff')}
          </Text>
          <Text size="xl" fw={800} style={{ color: '#475569' }}>{writtenOff.length}</Text>
        </Paper>
      </SimpleGrid>

      <Tabs value={activeTab} onChange={setActiveTab} radius="md">

        {/* ── Tab: Damaged ── */}
        <Tabs.Panel value="damaged">
          {damaged.length > 0 && (
            <Box style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 18px', marginBottom: 16 }}>
              <Group gap={10}>
                <IconAlertTriangle size={16} color="#dc2626" />
                <Box>
                  <Text size="sm" fw={600} c="red">
                    {damaged.length} {t('writeoff.pendingReview')}
                  </Text>
                  <Text size="xs" c="dimmed">{t('writeoff.pendingNote')}</Text>
                </Box>
              </Group>
            </Box>
          )}
          <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
            <Box style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <Text fw={600} size="sm">{t('writeoff.damagedAssets')}</Text>
            </Box>
            {loading ? <Center p="xl"><Loader color="teal" size="sm" /></Center>
              : filtered(damaged).length === 0 ? (
                <Center p="xl"><Stack align="center" gap={6}>
                  <IconCheck size={36} color="#10b981" />
                  <Text size="sm" c="dimmed">{t('writeoff.noDamaged')}</Text>
                </Stack></Center>
              ) : isMobile ? (
                // ── มือถือ: การ์ด — ปุ่ม "Write Off" หลักยังคงเห็นเสมอ (ไม่ซ่อนในเมนู เป็น action
                // หลักของแท็บนี้) ส่วนแก้ไขข้อมูลความเสียหาย + ไฟล์แนบ/รูปซ่อม/อะไหล่ที่ใช้ พับไว้
                // กดที่การ์ดเพื่อขยายดู (เนื้อหาซับซ้อนกว่าจะยัดในแถวเดียว เลยแยกเป็นการ์ดขยายได้) ──
                <Stack gap="xs" p="xs">
                  {filtered(damaged).map(a => <DamagedCard key={a.id} asset={a} />)}
                </Stack>
              ) : (
                <ScrollArea>
                  <Table highlightOnHover>
                    <Table.Thead style={{ background: '#f8f7f4' }}>
                      <Table.Tr>
                        <Table.Th style={thStyle}>{t('writeoff.assetCode')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.assetName')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.serialNo')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.department')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.location')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.damageReason')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.purchasePrice')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.status')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.attachedFile')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.repairPhotos')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.partsUsed')}</Table.Th>
                        <Table.Th></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filtered(damaged).map(a => (
                        <Table.Tr key={a.id}>
                          <Table.Td><Text fw={700} size="sm" style={{ cursor: "pointer", color: "#0d9488", textDecoration: "underline dotted" }} onClick={() => openQv(a.assetCode)}>{a.assetCode}</Text></Table.Td>
                          <Table.Td>
                            <Text size="sm">{a.name}</Text>
                            <Text size="xs" c="dimmed">{a.category}</Text>
                          </Table.Td>
                          <Table.Td><Text size="xs" c="dimmed">{a.serialNumber ?? '-'}</Text></Table.Td>
                          <Table.Td><Text size="sm">{a.departmentCode ?? '-'}</Text></Table.Td>
                          <Table.Td><Text size="xs" c="dimmed">{a.location ?? '-'}</Text></Table.Td>
                          <Table.Td>
                            <Group gap={4} wrap="nowrap">
                              <Tooltip label={a.damagedNote || '-'} disabled={!a.damagedNote} multiline w={260}>
                                <Text size="xs" c="dimmed" style={{ maxWidth: 200 }} lineClamp={2}>{a.damagedNote || '—'}</Text>
                              </Tooltip>
                              <Tooltip label={t('writeoff.editDamageInfo')}>
                                <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => openEditDamaged(a)}>
                                  <IconEdit size={12} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Table.Td>
                          <Table.Td><Text size="sm">{a.purchasePrice ? `฿${Number(a.purchasePrice).toLocaleString()}` : '-'}</Text></Table.Td>
                          <Table.Td><StatusBadge status={a.status} /></Table.Td>
                          <FileCell asset={a} />
                          <Table.Td><RepairPhotoStrip assetId={a.id} t={t} /></Table.Td>
                          <Table.Td><RepairPartsUsed assetCode={a.assetCode} /></Table.Td>
                          <Table.Td>
                            <Tooltip label={t('writeoff.generatePdf')}>
                              <Button size="xs" variant="light" color="red"
                                leftSection={<IconFileText size={13} />}
                                onClick={() => openWriteOff(a)}>
                                {t('writeoff.writeOff')}
                              </Button>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
          </Box>
        </Tabs.Panel>

        {/* ── Tab: Lost ── */}
        <Tabs.Panel value="lost">
          {lost.length > 0 && (
            <Box style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 18px', marginBottom: 16 }}>
              <Group gap={10}>
                <IconQuestionMark size={16} color="#d97706" />
                <Text size="sm" fw={600} c="yellow.8">
                  {lost.length} {t('writeoff.lostAssets')}
                </Text>
              </Group>
            </Box>
          )}
          <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
            {isMobile ? (
              lost.length === 0 ? (
                <Center p="xl"><Text size="sm" c="dimmed" py="xl">{t('writeoff.noLost')}</Text></Center>
              ) : (
                <Stack gap="xs" p="xs">
                  {lost.filter(a => {
                    const q = search.toLowerCase()
                    return !q || a.assetCode?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q) || a.serialNumber?.toLowerCase().includes(q)
                  }).map((a: any) => <LostCard key={a.id} asset={a} />)}
                </Stack>
              )
            ) : (
            <Table striped highlightOnHover>
              <Table.Thead style={{ background: '#f8fafc' }}>
                <Table.Tr>
                  <Table.Th>{t('writeoff.assetCode')}</Table.Th>
                  <Table.Th>{t('writeoff.name')}</Table.Th>
                  <Table.Th>{t('writeoff.department')}</Table.Th>
                  <Table.Th>{t('writeoff.lostDate')}</Table.Th>
                  <Table.Th>{t('writeoff.lostNote')}</Table.Th>
                  <Table.Th>{t('writeoff.lostIncidentFile')}</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {lost.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text size="sm" c="dimmed" ta="center" py="xl">{t('writeoff.noLost')}</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : lost.filter(a => {
                  const q = search.toLowerCase()
                  return !q || a.assetCode?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q) || a.serialNumber?.toLowerCase().includes(q)
                }).map((a: any) => (
                  <Table.Tr key={a.id}>
                    <Table.Td><Text size="sm" fw={600}>{a.assetCode}</Text></Table.Td>
                    <Table.Td><Text size="sm">{a.name}</Text><Text size="xs" c="dimmed">{a.serialNumber}</Text></Table.Td>
                    <Table.Td><Text size="sm">{a.departmentCode}</Text></Table.Td>
                    <Table.Td>
                      <Text size="sm">{a.lostDate ? new Date(a.lostDate).toLocaleDateString('en-GB') : '—'}</Text>
                    </Table.Td>
                    <Table.Td><Text size="sm" c="dimmed" style={{ maxWidth: 200 }} lineClamp={2}>{a.lostNote || '—'}</Text></Table.Td>
                    <Table.Td>
                      {a.lostIncidentUrl?.trim() ? (
                        <Group gap={6} wrap="nowrap">
                          <Anchor
                            href={`${BACKEND}/file-proxy?url=${encodeURIComponent(a.lostIncidentUrl)}&filename=${encodeURIComponent(a.lostIncidentFilename || `Incident_${a.assetCode}`)}`}
                            target="_blank" size="xs">
                            <Group gap={4}><IconPaperclip size={12} />{t('writeoff.viewFile')}</Group>
                          </Anchor>
                          <FileButton onChange={(f) => { if (f) handleUploadLostIncident(a, f) }} accept="*/*">
                            {(props) => (
                              <Tooltip label={t('writeoff.replaceFile')}>
                                <Button {...props} size="xs" variant="subtle" color="gray" p={4} loading={uploadingId === a.id}>
                                  <IconUpload size={12} />
                                </Button>
                              </Tooltip>
                            )}
                          </FileButton>
                        </Group>
                      ) : (
                        <FileButton onChange={(f) => { if (f) handleUploadLostIncident(a, f) }} accept="*/*">
                          {(props) => (
                            <Button {...props} size="xs" variant="light" color="gray"
                              leftSection={<IconUpload size={12} />}
                              loading={uploadingId === a.id}>
                              {t('writeoff.attachFile')}
                            </Button>
                          )}
                        </FileButton>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Button size="xs" color="red" variant="light"
                        onClick={() => openWriteOff(a)}>
                        {t('writeoff.writeOffLost')}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            )}
          </Box>
        </Tabs.Panel>

        {/* ── Tab: Written Off History ── */}
        <Tabs.Panel value="writtenoff">
          <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
            <Box style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <Group gap={8}>
                <IconClipboardX size={15} color="#475569" />
                <Text fw={600} size="sm">{t('writeoff.historyTitle')}</Text>
              </Group>
            </Box>
            {loading ? <Center p="xl"><Loader color="teal" size="sm" /></Center>
              : filtered(writtenOff).length === 0 ? (
                <Center p="xl"><Stack align="center" gap={6}>
                  <IconHistory size={36} color="#cbd5e1" />
                  <Text size="sm" c="dimmed">{t('writeoff.noHistory')}</Text>
                </Stack></Center>
              ) : isMobile ? (
                <Stack gap="xs" p="xs">
                  {filtered(writtenOff).map(a => <WrittenOffCard key={a.id} asset={a} />)}
                </Stack>
              ) : (
                <ScrollArea>
                  <Table highlightOnHover>
                    <Table.Thead style={{ background: '#f8f7f4' }}>
                      <Table.Tr>
                        <Table.Th style={thStyle}>{t('writeoff.assetCode')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.assetName')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.serialNo')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.department')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.reason')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.writtenOffBy')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.writtenOffDate')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.attachedFile')}</Table.Th>
                        <Table.Th style={thStyle}>{t('writeoff.repairPhotos')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filtered(writtenOff).map(a => (
                        <Table.Tr key={a.id}>
                          <Table.Td><Text fw={700} size="sm" style={{ cursor: "pointer", color: "#0d9488", textDecoration: "underline dotted" }} onClick={() => openQv(a.assetCode)}>{a.assetCode}</Text></Table.Td>
                          <Table.Td>
                            <Text size="sm">{a.name}</Text>
                            <Text size="xs" c="dimmed">{a.category}</Text>
                          </Table.Td>
                          <Table.Td><Text size="xs" c="dimmed">{a.serialNumber ?? '-'}</Text></Table.Td>
                          <Table.Td><Text size="sm">{a.departmentCode ?? '-'}</Text></Table.Td>
                          <Table.Td>
                            <Tooltip
                              label={
                                <Stack gap={2}>
                                  {a.damagedNote && <Text size="xs">Damage: {a.damagedNote}</Text>}
                                  <Text size="xs">Write-Off: {a.writeOffReason || '-'}</Text>
                                </Stack>
                              }
                              multiline w={280}
                            >
                              <Box style={{ maxWidth: 200 }}>
                                <Text size="xs" lineClamp={2}>
                                  {a.writeOffReason || <Text span c="dimmed">—</Text>}
                                </Text>
                                {a.damagedNote && (
                                  <Text size="xs" c="dimmed" lineClamp={1} mt={1}>🛠 {a.damagedNote}</Text>
                                )}
                              </Box>
                            </Tooltip>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4}>
                              <IconUser size={12} color="#94a3b8" />
                              <Text size="xs">{a.writeOffBy ?? '—'}</Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4}>
                              <IconCalendar size={12} color="#94a3b8" />
                              <Text size="xs">
                                {a.writeOffAt
                                  ? new Date(a.writeOffAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                  : '—'}
                              </Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            {a.damagedIncidentUrl
                              ? <Anchor href={incidentDownloadUrl(a.damagedIncidentUrl, a.assetCode, a.damagedIncidentFilename)} target="_blank" size="xs">
                                  <Group gap={4}><IconPaperclip size={12} />{t('writeoff.viewFile')}</Group>
                                </Anchor>
                              : <Text size="xs" c="dimmed">—</Text>
                            }
                          </Table.Td>
                          <Table.Td><RepairPhotoStrip assetId={a.id} t={t} /></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
          </Box>
        </Tabs.Panel>
      </Tabs>

      {/* ── Write Off Modal ── */}
      <Modal opened={opened} onClose={close} size="lg" radius="lg"
        title={
          <Group gap={8}>
            <IconAlertTriangle size={18} color="#dc2626" />
            <Text fw={600}>{t('writeoff.modalSubtitle')} {selected?.assetCode}</Text>
          </Group>
        }>
        <Stack gap="md">
          <Box style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px' }}>
            <SimpleGrid cols={2} spacing="xs">
              <Box><Text size="xs" c="dimmed">{t('writeoff.assetName')}</Text><Text size="sm" fw={600}>{selected?.name}</Text></Box>
              <Box><Text size="xs" c="dimmed">{t('writeoff.serialNo')}</Text><Text size="sm">{selected?.serialNumber ?? '-'}</Text></Box>
              <Box><Text size="xs" c="dimmed">{t('writeoff.department')}</Text><Text size="sm">{selected?.departmentCode ?? '-'}</Text></Box>
              <Box><Text size="xs" c="dimmed">{t('writeoff.purchasePrice')}</Text><Text size="sm">{selected?.purchasePrice ? `฿${Number(selected.purchasePrice).toLocaleString()}` : '-'}</Text></Box>
            </SimpleGrid>
          </Box>

          <Divider label={t('writeoff.writeOffDetails')} labelPosition="left" />

          <Textarea
            label={`${t('writeoff.reason')} *`}
            placeholder={t('writeoff.reasonPlaceholder')}
            minRows={3}
            value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
          />
          <Textarea
            label={t('writeoff.detail')}
            placeholder={t('writeoff.detailPlaceholder')}
            minRows={2}
            value={form.detail} onChange={e => setForm(p => ({ ...p, detail: e.target.value }))}
          />
          <TextInput
            label={t('writeoff.maintenanceRef')}
            placeholder="RPR-00006"
            value={form.maintenanceRef} onChange={e => setForm(p => ({ ...p, maintenanceRef: e.target.value }))}
          />

          {/* ── Incident File ── */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Incident Report{' '}
              <Text span size="xs" c="dimmed">(PDF, JPG, PNG)</Text>
            </Text>
            {selected?.damagedIncidentUrl && !incidentFile && (
              <Group gap={6} mb={6}>
                <IconPaperclip size={13} color="#64748b" />
                <Anchor href={incidentDownloadUrl(selected.damagedIncidentUrl, selected.assetCode, selected.damagedIncidentFilename)} target="_blank" size="xs">
                  {t('writeoff.viewExisting')}
                </Anchor>
                <Text size="xs" c="dimmed">{t('writeoff.willBeReplaced')}</Text>
              </Group>
            )}
            {incidentFile ? (
              <Group gap={8} p="xs" style={{ background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <IconPaperclip size={14} color="#16a34a" />
                <Text size="xs" style={{ flex: 1 }} lineClamp={1}>{incidentFile.name}</Text>
                <Text size="xs" c="dimmed">({(incidentFile.size / 1024).toFixed(0)} KB)</Text>
                <Button size="xs" variant="subtle" color="red" p={4}
                  onClick={() => { setIncidentFile(null); resetIncidentRef.current?.() }}>
                  <IconX size={13} />
                </Button>
              </Group>
            ) : (
              <FileButton resetRef={resetIncidentRef}
                onChange={(f) => { if (f) setIncidentFile(f) }}
                accept="application/pdf,image/jpeg,image/png,image/webp">
                {(props) => (
                  <Button {...props} size="xs" variant="light" color="gray" leftSection={<IconUpload size={13} />}>
                    {t('writeoff.attachFile')}
                  </Button>
                )}
              </FileButton>
            )}
          </Box>

          <Box style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px' }}>
            <Text size="xs" fw={600} c="orange" mb={4}>{t('writeoff.processNote')}</Text>
            <Text size="xs" c="dimmed">
              1. {t('writeoff.step1')}<br />
              2. {t('writeoff.step1b')}<br />
              3. {t('writeoff.step2')}<br />
              4. {t('writeoff.step3')}<br />
              5. {t('writeoff.step5')}
            </Text>
          </Box>

          <Group justify="flex-end">
            <Button variant="default" onClick={close}>{t('common.cancel')}</Button>
            <Button color="red" leftSection={<IconFileText size={15} />}
              loading={saving} onClick={handleConfirmWriteOff}>
              {t('writeoff.generateAndWriteOff')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Damaged Reason / Note */}
      <Modal
        opened={!!editingDamaged}
        onClose={() => setEditingDamaged(null)}
        title={<Text fw={600}>{t('writeoff.editDamageInfo')} — {editingDamaged?.assetCode}</Text>}
        size="sm" centered
      >
        <Stack gap="sm">
          <SmartDateInput
            label={t('writeoff.damagedDate')}
            placeholder={t("license.datePlaceholder","DD/MM/YYYY")}
            value={editDamagedForm.damagedDate}
            onChange={v => setEditDamagedForm(p => ({ ...p, damagedDate: v }))}
          />
          <Textarea
            label={t('writeoff.damageReason')}
            placeholder={t('writeoff.damageReasonPlaceholder') ?? ''}
            minRows={3}
            value={editDamagedForm.damagedNote}
            onChange={e => setEditDamagedForm(p => ({ ...p, damagedNote: e.target.value }))}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setEditingDamaged(null)} disabled={savingDamaged}>
              {t('common.cancel')}
            </Button>
            <Button color="teal" leftSection={<IconCheck size={15} />} loading={savingDamaged} onClick={saveDamagedInfo}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <AssetQuickView asset={qvAsset} opened={qvOpen} onClose={() => setQvOpen(false)} />
    </Box>
  )
}
